import { useEffect, useMemo, useState } from "react"

import type {
    FieldValues,
    UseFormReturn,
    UseFormSetValue,
    UseFormWatch,
} from "react-hook-form"

import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import { AvailableLookupConsumer } from "@common-shared/config/lookup.config.ts"
import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import type { IPayer } from "@common-shared/types/responseData/commonResponseData.types.ts"

import { FRONT_REGEX_CONFIG } from "@front-shared/config/regex.config.ts"
import { useCptCodes } from "@front-shared/hooks/cptCodes.hook.tsx"

import { LookupHelper } from "../helpers/lookup.helper"
import { useAppDispatch } from "../store/appDispatch.hook"
import { useAppSelector } from "../store/selectors/app.selector"
import { checkerSelector } from "../store/selectors/checker.selector"
import { coveragePayersSelector } from "../store/selectors/coveragePayers.selector"
import { selectedPracticeRoleSelector } from "../store/selectors/selectedPracticeRole.selector"
import {
    checkerSetIsCheckTypeRelatedDataEntered,
    checkerSetManualCheckType,
} from "../store/slices/checker.slice"
import type { IDynamicCheckerFormInputData } from "../types/data.types"
import { useCoveragePayerSelection } from "./coveragePayerSelection.hooks"
import { useIsFeatureFlagEnabled } from "./isFeatureFlagEnabled.hook"

export const useCheckerDynamicForm = (
    formMethods: UseFormReturn<IDynamicCheckerFormInputData>,
    isAgenticCheck: boolean,
) => {
    const dispatch = useAppDispatch()

    const { setValue, watch } = formMethods

    const dataWatcher = watch()

    const {
        continuousMonitoringCoverageChecks,
        manualCheckType,
        medicaidChecks,
    } = useAppSelector(checkerSelector)

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const coveragePayers = useAppSelector(coveragePayersSelector)

    const scanCheckEligible = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_SCAN_MANUAL_CHECK,
    )

    const discoverCheckEligible = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_DISCOVER_MANUAL_CHECK,
    )

    const medicaidCheckEligible = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_MEDICAID_MANUAL_CHECK,
    )

    const { availableCptCodes } = useCptCodes(
        AvailableLookupConsumer.coveragePortal,
        selectedPracticeRole?.availableModalities,
    )

    const { onPayerSelected } = useCoveragePayerSelection(
        coveragePayers,
        availableCptCodes,
        setValue as unknown as UseFormSetValue<FieldValues>,
        watch as unknown as UseFormWatch<FieldValues>,
        true,
        undefined,
        manualCheckType,
    )

    const [payerInputKey, setPayerInputKey] = useState<string>(
        `payer-${new Date().getTime()}`,
    )

    const [memberIdInputKey, setMemberIdInputKey] = useState<string>(
        `memberId-${new Date().getTime()}`,
    )

    const [initialDataWasSet, setInitialDataWasSet] = useState<boolean>(false)
    const [previousCheckType, setPreviousCheckType] = useState<QuotaUsageType>()

    const secondaryDataWasSet = useMemo<boolean>(() => {
        return (
            !!dataWatcher.state ||
            (!!dataWatcher.zipCode &&
                FRONT_REGEX_CONFIG.zipShort.test(dataWatcher.zipCode)) ||
            ((manualCheckType === QuotaUsageType.GENERAL || isAgenticCheck) &&
                !!dataWatcher.payer &&
                !!dataWatcher.memberId)
        )
    }, [dataWatcher, manualCheckType, isAgenticCheck])

    const firstCheckInStateInputData = useMemo<
        IDynamicCheckerFormInputData | undefined
    >(() => {
        return (continuousMonitoringCoverageChecks[0] || medicaidChecks[0])
            ?.inputData
    }, [continuousMonitoringCoverageChecks[0], medicaidChecks[0]])

    const payerSupportsDiscover = useMemo<boolean>(() => {
        return !!dataWatcher.payer && dataWatcher.payer.isDiscoverSupported
    }, [dataWatcher.payer])

    // Automatically identify manual check type
    // No payer -> scan
    // Payer and no memberId -> discover
    // If medicaid payer -> medicaid
    // Payer and memberId -> standard
    // Debounce this so it doesn't conflict with manual selection
    useEffect(() => {
        // Don't do anything if we already have manual check type set to some agentic
        if (
            manualCheckType &&
            [
                QuotaUsageType.AGENTIC_PORTAL,
                QuotaUsageType.AGENTIC_PHONE,
            ].includes(manualCheckType)
        ) {
            return
        }

        const timeoutId = setTimeout(() => {
            let checkType: QuotaUsageType | undefined

            if (scanCheckEligible && dataWatcher.payerNotAvailable) {
                checkType = QuotaUsageType.SMART_SCAN
            } else if (
                medicaidCheckEligible &&
                isMedicaidPayerSelected(dataWatcher.payer)
            ) {
                checkType = QuotaUsageType.MEDICAID
            } else if (
                discoverCheckEligible &&
                dataWatcher.payer &&
                dataWatcher.memberIdNotAvailable
            ) {
                checkType = QuotaUsageType.DISCOVER
            } else if (dataWatcher.payer) {
                checkType = QuotaUsageType.GENERAL
            }

            // Reset medicaid, if payer is not medicaid and we didn't identify new check type yet
            if (
                !checkType &&
                manualCheckType === QuotaUsageType.MEDICAID &&
                checkType !== QuotaUsageType.MEDICAID &&
                dataWatcher.payer &&
                !isMedicaidPayerSelected(dataWatcher.payer)
            ) {
                dispatch(checkerSetManualCheckType(undefined))
            } else if (checkType) {
                dispatch(checkerSetManualCheckType(checkType))
            }
        }, 300)

        return () => {
            clearTimeout(timeoutId)
        }
    }, [dataWatcher])

    // if manually removing check type -> need to reset payerNotAvailable and memberIdNotAvailable
    // if manually set smart scan -> need to disable payer
    // if manually set discover -> need to disable memberId
    // if manually set medicaid and selected payer is not medicaid -> need to select medicaid payer
    useEffect(() => {
        switch (manualCheckType) {
            case QuotaUsageType.SMART_SCAN:
                setValue("payerNotAvailable", true)
                resetPayerInput()

                break

            case QuotaUsageType.DISCOVER:
                if (
                    previousCheckType === QuotaUsageType.MEDICAID ||
                    isMedicaidPayerSelected(dataWatcher.payer)
                ) {
                    resetPayerInput()
                }

                setValue("memberId", undefined)
                setValue("payerNotAvailable", false)
                setValue("memberIdNotAvailable", true)

                break

            case QuotaUsageType.MEDICAID:
                if (!isMedicaidPayerSelected(dataWatcher.payer)) {
                    resetPayerInput(LookupHelper.getMedicaidDefaultPayer())
                }

                setValue("payerNotAvailable", false)

                break

            case QuotaUsageType.GENERAL:
            case undefined:
                if (
                    !manualCheckType ||
                    previousCheckType === QuotaUsageType.MEDICAID ||
                    (medicaidCheckEligible &&
                        isMedicaidPayerSelected(dataWatcher.payer))
                ) {
                    resetPayerInput()
                }

                if (previousCheckType === QuotaUsageType.MEDICAID) {
                    resetMemberIdInput()
                }

                setValue("payerNotAvailable", false)
                setValue("memberIdNotAvailable", false)

                break
        }

        setPreviousCheckType(manualCheckType)
    }, [manualCheckType])

    // Monitor that user provided initial data, and then enable other fields
    // Should be triggered only once - if user removes some data shouldn't hide other fields
    useEffect(() => {
        if (initialDataWasSet) {
            return
        }

        setInitialDataWasSet(
            !!dataWatcher.firstName &&
                !!dataWatcher.lastName &&
                !!dataWatcher.dob,
        )
    }, [dataWatcher])

    // Monitor that user provided state/memberId/zip + check type was identified
    // in this case we need to set in state variable that will be used when closing checker
    // will be used in confirmation for removing active check type
    useEffect(() => {
        dispatch(
            checkerSetIsCheckTypeRelatedDataEntered(
                !!manualCheckType &&
                    !!(
                        dataWatcher.state ||
                        dataWatcher.memberId ||
                        dataWatcher.zipCode
                    ),
            ),
        )
    }, [dataWatcher.state, dataWatcher.memberId, manualCheckType])

    const isMedicaidPayerSelected = (payer?: IPayer) => {
        return payer?.insuranceName.toLowerCase().includes("medicaid")
    }

    const resetPayerInput = (payer?: IPayer) => {
        setValue("payer", payer)

        setTimeout(() => {
            setPayerInputKey(`payer-${new Date().getTime()}`)
        })
    }

    const resetMemberIdInput = () => {
        setValue("memberId", undefined)

        setTimeout(() => {
            setMemberIdInputKey(`memberId-${new Date().getTime()}`)
        })
    }

    // Apply a payer predicted from the member ID: set the form value (which also
    // records it as the applied prediction via resetPayerInput) and run the
    // standard payer-selected side effects.
    const applyPredictedPayer = (payer: IPayer) => {
        resetPayerInput(payer)
        onPayerSelected(payer)
    }

    return {
        applyPredictedPayer,
        formDefaultValues: firstCheckInStateInputData,
        formDefaultValuesContinuousMonitoring:
            continuousMonitoringCoverageChecks[0]?.inputData,
        formDefaultValuesMedicaid: medicaidChecks[0]?.inputData,
        initialDataWasSet: initialDataWasSet || !!manualCheckType,
        isMedicaidPayerSelected,
        memberIdInputKey,
        onPayerSelected,
        payerInputKey,
        payerSupportsDiscover,
        secondaryDataWasSet,
    }
}
